import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Info, Shield, X } from 'lucide-react';
import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { fetchMe } from '../features/auth/api';
import { installForegroundPushHandler, syncFirebasePushIfGranted } from '../features/push/firebasePush';
import { PwaInstallGate } from '../features/pwa/PwaInstallGate';
import { AppUpdatePrompt } from '../features/update/AppUpdatePrompt';
import { useAuthStore } from '../features/auth/authStore';
import { RaffleFlowOverlay } from '../features/raffle/RaffleFlowOverlay';
import { useHallPresence } from '../features/presence/useHallPresence';
import { fetchMaintenance, type MaintenanceStatus } from '../features/maintenance/api';
import { BrandOwl, BrandWordmark } from '../shared/brand/BrandOwl';
import { useCountryStore } from '../shared/country/countryStore';
import { useI18nStore } from '../shared/i18n/i18nStore';
import { BottomNav } from '../shared/navigation/BottomNav';
import { useAppStore } from './appStore';
import type { MeSecurityTargetChannel, PlayerRoute, WalletTargetAction, WalletTicketsTargetView } from './appStore';

// First-visit 18+ gate (PRD §10.1): blocking overlay, localStorage-acked,
// compliance copy links to Jogo Responsável.
function AgeGate() {
  const t = useI18nStore((state) => state.t);
  useI18nStore((state) => state.language);
  const [acked, setAcked] = useState(() => {
    try {
      return window.localStorage.getItem('fd:age18Acked') === '1';
    } catch {
      return true;
    }
  });
  if (acked) return null;
  return (
    <div className="ds-agegate" role="dialog" aria-modal="true" aria-label={t('ageGateTitle')} data-age-gate="true">
      <div className="ds-agegate-card">
        <span className="ds-agegate-badge">18+</span>
        <h2>{t('ageGateTitle')}</h2>
        <p>{t('ageGateBody')}</p>
        <button
          className="ds-btn ds-btn-p tap"
          type="button"
          onClick={() => {
            try {
              window.localStorage.setItem('fd:age18Acked', '1');
            } catch {
              /* private mode: allow through, gate reappears next visit */
            }
            setAcked(true);
          }}
        >
          {t('ageGateConfirm')}
        </button>
        <a className="ds-agegate-link" href="/responsavel.html" target="_blank" rel="noopener">{t('legalResponsible')}</a>
      </div>
    </div>
  );
}

// Eager static imports: every page is in the main bundle, so navigating never
// waits on a lazy chunk — the Suspense fallback ("Preparing session") no longer
// flashes between page switches. (The one-time startup screen below, gated on
// hydration + the session check, is separate and stays.)
import { AuthPage } from '../features/auth/AuthPage';
import { ArchivePage } from '../pages/archive/ArchivePage';
import { RulesPage } from '../pages/archive/RulesPage';
import { MilharLedgerPage } from '../pages/milhar/MilharLedgerPage';
import { MilharStatsPage } from '../pages/milhar/MilharStatsPage';
import { MilharPage } from '../pages/milhar/MilharPage';
import { SorteiosPage } from '../pages/sorteios/SorteiosPage';
import { SorteiosHistoryPage } from '../pages/sorteios/SorteiosHistoryPage';
import { LobbyPage } from '../pages/lobby/LobbyPage';
import { LobbyNextPage } from '../pages/lobby/LobbyNextPage';
import { RoomPage } from '../pages/room/RoomPage';
import { AvisosPage } from '../pages/tabs/AvisosPage';
import { AuditProgressPage } from '../pages/tabs/AuditProgressPage';
import { FollowsPage } from '../pages/tabs/FollowsPage';
import { LeaderboardPage } from '../pages/tabs/LeaderboardPage';
import { MePage } from '../pages/tabs/MePage';
import { RewardsPage } from '../pages/tabs/RewardsPage';
import { TransactionHistoryPage } from '../pages/tabs/TransactionHistoryPage';
import { UnpaidDepositsPage } from '../pages/tabs/UnpaidDepositsPage';
import { WalletPage } from '../pages/tabs/WalletPage';

export function App() {
  const route = useAppStore((state) => state.route);
  const setRoute = useAppStore((state) => state.setRoute);
  const hydrateFromBrowser = useAppStore((state) => state.hydrateFromBrowser);
  const toast = useAppStore((state) => state.toast);
  const clearToast = useAppStore((state) => state.clearToast);
  const showToast = useAppStore((state) => state.showToast);
  const token = useAuthStore((state) => state.token);
  const guestMode = useAuthStore((state) => state.guestMode);
  const enterGuest = useAuthStore((state) => state.enterGuest);
  const hydrated = useAuthStore((state) => state.hydrated);
  const user = useAuthStore((state) => state.user);
  const setUser = useAuthStore((state) => state.setUser);
  const markHydrated = useAuthStore((state) => state.markHydrated);
  const logout = useAuthStore((state) => state.logout);
  const refreshSession = useAuthStore((state) => state.refreshSession);
  const clearGuest = useAuthStore((state) => state.clearGuest);
  const t = useI18nStore((state) => state.t);
  const language = useI18nStore((state) => state.language);
  const adoptServerLocale = useI18nStore((state) => state.adoptServerLocale);
  const adoptAccountCountry = useCountryStore((state) => state.adoptAccountCountry);
  const loadCountries = useCountryStore((state) => state.load);
  const loadCountrySuggestion = useCountryStore((state) => state.loadSuggestion);
  // Subscribes the whole tree to the active country. The money formatters resolve
  // it imperatively (so ~195 call sites need no country argument), which means
  // nothing would re-render when it changes — an Argentine account would keep
  // showing R$ after sign-in adopted AR. See shared/lib/format.ts.
  useCountryStore((state) => state.country);
  const usernameReturnRef = useRef<UsernameReturnTarget | null>(null);

  // "N playing right now" on the lobby counts open clients, so every open
  // client has to say it is here — not just the ones standing in a room.
  useHallPresence();

  // Every session needs the country list, not just the ones that pass through the
  // auth screens. Loading it only in AuthPage meant an already-signed-in player
  // never fetched it: `countries` stayed empty, so the effective country fell back
  // to Brazil and an Argentine account saw its balance in R$ — with the stored
  // AR selection sitting right there, unused.
  useEffect(() => {
    // Concurrent, not sequential: the two calls are independent of each other.
    // `load` lands with whatever `suggestedCode` has already arrived, and
    // `loadSuggestion` lands with whatever `countries` has already arrived —
    // either order produces the right `country`.
    void loadCountries();
    void loadCountrySuggestion();
  }, [loadCountries, loadCountrySuggestion]);

  useEffect(() => {
    const syncRoute = () => hydrateFromBrowser();
    window.addEventListener('popstate', syncRoute);
    window.addEventListener('hashchange', syncRoute);
    hydrateFromBrowser();
    return () => {
      window.removeEventListener('popstate', syncRoute);
      window.removeEventListener('hashchange', syncRoute);
    };
  }, [hydrateFromBrowser]);

  const meQuery = useQuery({
    queryKey: ['me', token],
    queryFn: () => fetchMe(token),
    enabled: !!token && !guestMode,
    retry: false,
    // Re-validate the session when the tab regains focus. A page left open for
    // hours has an expired access token; public reads still work, so the app
    // looks logged in until a bet 401s. Refetching /api/me on focus surfaces the
    // stale token → the isError effect below silently refreshes (or logs out).
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });

  const maintenanceQuery = useQuery({
    queryKey: ['maintenance'],
    queryFn: fetchMaintenance,
    refetchInterval: 30_000,
    retry: false,
  });

  useEffect(() => {
    if (!token || guestMode) {
      markHydrated();
      return;
    }
    if (meQuery.data?.user) {
      setUser(meQuery.data.user);
      // Only lands on a device that has never picked a language, so someone who
      // registered in Spanish does not get Portuguese on a fresh phone.
      adoptServerLocale(meQuery.data.user.locale);
      // Opposite precedence: the account's country always wins. Signing in by
      // email or username carries no country, so without this an Argentine
      // account would keep whatever was selected for the phone prefix and format
      // its balance as BRL.
      adoptAccountCountry(meQuery.data.user.country_code);
    }
    // A /api/me failure usually means the 30-min access token expired. Try a
    // silent refresh first (works while the 30-day session is alive) — only log
    // out if the session itself is gone (refresh returns false). On success the
    // token rotates, meQuery's key changes and it refetches with the fresh token.
    if (meQuery.isError) {
      void refreshSession().then((ok) => {
        if (!ok) logout();
      });
    }
  }, [adoptAccountCountry, adoptServerLocale, guestMode, logout, refreshSession, markHydrated, meQuery.data, meQuery.isError, setUser, token]);

  // Proactive keeper: while the tab is visible, refresh the access token a few
  // minutes before it expires so active users (placing bets) never hit a 401.
  useEffect(() => {
    if (!token || guestMode) return;
    const REFRESH_MARGIN_SEC = 5 * 60;
    const check = () => {
      if (document.visibilityState !== 'visible') return;
      const exp = useAuthStore.getState().expiresAt;
      if (!exp) return;
      if (Date.now() / 1000 >= exp - REFRESH_MARGIN_SEC) {
        void refreshSession().then((ok) => {
          if (!ok) logout();
        });
      }
    };
    check();
    const id = window.setInterval(check, 60_000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [token, guestMode, refreshSession, logout]);

  // Never prompt on page load. A player who already granted permission gets
  // the current Firebase installation re-bound after login/token rotation.
  useEffect(() => {
    if (!token || guestMode) return;
    let dispose: () => void = () => {};
    void syncFirebasePushIfGranted(token).catch(() => undefined);
    void installForegroundPushHandler(token, (payload) => {
      const text = payload.notification?.body || payload.notification?.title;
      if (text) showToast(text, 'info', { ms: 6000 });
    }).then((cleanup) => { dispose = cleanup; }).catch(() => undefined);
    return () => dispose();
  }, [guestMode, showToast, token]);

  useEffect(() => {
    if (!hydrated) return;
    // The public hall is the default entry for a fresh visitor. Use the same
    // guest state as the existing guest button, retaining all sign-in guards
    // for account actions and the separate age gate.
    if (!token && !guestMode && route === 'lobby') {
      enterGuest();
      return;
    }
    if (!token && route === 'auth.username') setRoute('auth.login', { replace: true });
    else if (!token && !guestMode && !isAuthRoute(route) && !isPublicRoute(route)) setRoute('auth.login', { replace: true });
    else if (token && userRequiresUsernameSetup(user) && route !== 'auth.username') {
      usernameReturnRef.current = captureUsernameReturnTarget();
      setRoute('auth.username', { replace: true });
    }
    else if ((token || guestMode) && isAuthRoute(route) && route !== 'auth.username') setRoute('lobby', { replace: true });
    else if (token && route === 'auth.username' && !userRequiresUsernameSetup(user)) setRoute('lobby', { replace: true });
  }, [enterGuest, guestMode, hydrated, route, setRoute, token, user]);

  const completeUsernameSetup = useCallback(() => {
    const target = usernameReturnRef.current;
    usernameReturnRef.current = null;
    restoreUsernameReturnTarget(target);
  }, []);

  // Boot splash only while we have NO user yet (cold start). Token rotation
  // changes meQuery's key (['me', token]) and flips isLoading on the fresh
  // cache entry — without the !user guard that unmounted the whole app tree
  // (losing sheet/input/scroll state) and flashed this splash every ~25min
  // of active use and on every silent session recovery.
  if (!hydrated || (!token && !guestMode && route === 'lobby') || (!!token && !guestMode && meQuery.isLoading && !user)) {
    return (
      <main className="app-shell">
        <PwaInstallGate />
        <section className="screen center-screen">
          <BootSplash />
        </section>
      </main>
    );
  }

  const authenticated = !!token || guestMode || isPublicRoute(route);
  const showSessionReminder = authenticated && !isAuthRoute(route) && !isPublicRoute(route);
  // The redesigned hall carries Log in / Sign up in its own header, so the
  // banner would be a second, louder copy of the same offer stacked above it.
  // Both routes that render it are excluded; the legacy hall keeps its banner.
  const isNewHall = route === 'lobby' || route === 'lobby.next';
  const showGuestBanner = guestMode && !isAuthRoute(route) && !isPublicRoute(route) && !isNewHall;

  return (
    <main className={`app-shell ${showGuestBanner ? 'has-guest-banner' : ''}`}>
      <PwaInstallGate />
      <AgeGate />
      {showGuestBanner ? (
        <GuestBanner
          onSignup={() => {
            clearGuest();
            setRoute('auth.register', { replace: true });
          }}
        />
      ) : null}
      <section className="screen">
        <Suspense fallback={<RouteFallback />}>
          {authenticated ? <AuthenticatedRoute route={route} onUsernameComplete={completeUsernameSetup} /> : <AuthPage onAuthenticated={() => setRoute('lobby')} />}
        </Suspense>
      </section>
      <RaffleFlowOverlay />
      <AppUpdatePrompt />
      <TopOverlays
        maintenanceStatus={maintenanceQuery.data}
        language={language}
        toast={toast}
        onClearToast={clearToast}
        showSessionReminder={showSessionReminder}
        onOpenLimits={() => setRoute('me.limits')}
      />
    </main>
  );
}

function TopOverlays({
  maintenanceStatus,
  language,
  toast,
  onClearToast,
  showSessionReminder,
  onOpenLimits,
}: {
  maintenanceStatus?: MaintenanceStatus;
  language: ReturnType<typeof useI18nStore.getState>['language'];
  toast: ReturnType<typeof useAppStore.getState>['toast'];
  onClearToast: ReturnType<typeof useAppStore.getState>['clearToast'];
  showSessionReminder: boolean;
  onOpenLimits: () => void;
}) {
  return (
    <div className="top-overlays-react" data-top-overlays-react="true">
      <MaintenanceBanner status={maintenanceStatus} language={language} />
      <ToastOverlay toast={toast} onClear={onClearToast} />
      {showSessionReminder ? <SessionReminder onOpenLimits={onOpenLimits} /> : null}
    </div>
  );
}

// Boot / route-loading splash in the current brand: owl mark + wordmark +
// a slim gold shimmer bar (the old gold "S" square was the retired logo).
function BootSplash() {
  const t = useI18nStore((state) => state.t);
  return (
    <div className="boot-splash" role="status" aria-live="polite">
      <BrandOwl size={72} className="boot-splash-owl" />
      <BrandWordmark height={20} />
      <span className="boot-splash-bar" aria-hidden="true"><i /></span>
      <p className="boot-splash-caption">{t('authPreparing')}</p>
    </div>
  );
}

function RouteFallback() {
  return (
    <div className="route-loading-react">
      <BootSplash />
    </div>
  );
}

function GuestBanner({ onSignup }: { onSignup: () => void }) {
  const t = useI18nStore((state) => state.t);
  return (
    <div className="guest-banner-react" role="status" data-guest-banner-react="true">
      <span className="guest-banner-dot-react" aria-hidden="true" />
      <span className="guest-banner-text-react">{t('guestBanner')}</span>
      <button type="button" className="guest-banner-cta-react" onClick={onSignup}>{t('guestBannerCTA')}</button>
    </div>
  );
}

function ToastOverlay({
  toast,
  onClear,
}: {
  toast: ReturnType<typeof useAppStore.getState>['toast'];
  onClear: ReturnType<typeof useAppStore.getState>['clearToast'];
}) {
  const t = useI18nStore((state) => state.t);
  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => onClear(toast.id), toast.ms);
    return () => window.clearTimeout(timer);
  }, [onClear, toast]);

  if (!toast) return null;

  return (
    <div className={`toast-popup-react kind-${toast.kind}`} role="status" aria-live="polite" data-toast-overlay-react="true">
      <span className="toast-icon-react" aria-hidden="true">
        {toast.kind === 'loading' ? <span className="toast-spinner-react" /> : toast.kind === 'success' ? <CheckCircle2 size={16} /> : toast.kind === 'warn' || toast.kind === 'error' ? <AlertTriangle size={16} /> : <Info size={16} />}
      </span>
      <span>{toast.text}</span>
      <button type="button" onClick={() => onClear(toast.id)} aria-label={t('close')}>
        <X size={14} aria-hidden="true" />
      </button>
    </div>
  );
}

function MaintenanceBanner({ status, language }: { status?: MaintenanceStatus; language: ReturnType<typeof useI18nStore.getState>['language'] }) {
  const t = useI18nStore((state) => state.t);
  const level = status?.level || 'normal';
  if (!level || level === 'normal') return null;
  const messages = status?.message_i18n || status?.messageI18n || {};
  const message = messages[language] || messages[language === 'pt-BR' ? 'pt' : language] || messages.en || messages['pt-BR'] || t('maintenanceDefault');

  return (
    <div className={`maintenance-banner-react level-${level}`} role="status" aria-live="polite" data-maintenance-overlay-react="true">
      <AlertTriangle size={18} aria-hidden="true" />
      <span>
        <strong>{t('maintenanceTitle')}</strong>
        <small>{message}</small>
      </span>
    </div>
  );
}

function SessionReminder({ onOpenLimits }: { onOpenLimits: () => void }) {
  const t = useI18nStore((state) => state.t);
  const [startedAt] = useState(() => Date.now());
  const [minutes, setMinutes] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const tick = () => setMinutes(Math.floor((Date.now() - startedAt) / 60_000));
    tick();
    const timer = window.setInterval(tick, 30_000);
    return () => window.clearInterval(timer);
  }, [startedAt]);

  const threshold = sessionReminderThresholdMinutes();
  if (dismissed || minutes < threshold) return null;
  const time = formatSessionReminderTime(minutes);

  return (
    <div className="session-reminder-backdrop" role="presentation" data-session-reminder-react="true">
      <section className="session-reminder-card" role="dialog" aria-modal="true" aria-label={t('sessionReminderTitle')}>
        <div className="session-reminder-head">
          <span><Shield size={22} aria-hidden="true" /></span>
          <div>
            <h2>{t('sessionReminderTitle')}</h2>
            <p>{t('sessionReminderBody').replace('{{time}}', time).replace('%time%', time)}</p>
          </div>
        </div>
        <div className="session-reminder-actions">
          <button type="button" onClick={() => setDismissed(true)}>{t('sessionReminderContinue')}</button>
          <button type="button" onClick={() => { setDismissed(true); onOpenLimits(); }}>{t('sessionReminderLimits')}</button>
        </div>
      </section>
    </div>
  );
}

function sessionReminderThresholdMinutes() {
  try {
    return new URLSearchParams(window.location.search || '').has('qa_session_reminder') ? 1 : 60;
  } catch {
    return 60;
  }
}

function formatSessionReminderTime(minutes: number) {
  const safeMinutes = Math.max(0, Math.floor(minutes));
  if (safeMinutes < 60) return `${safeMinutes} min`;
  const hours = Math.floor(safeMinutes / 60);
  const mins = safeMinutes % 60;
  return mins ? `${hours}h ${mins}m` : `${hours}h`;
}

function AuthenticatedRoute({ route, onUsernameComplete }: { route: PlayerRoute; onUsernameComplete: () => void }) {
  if (route === 'auth.username') return <AuthPage onAuthenticated={onUsernameComplete} />;
  if (route === 'room') return <RoomPage />;
  if (route === 'rules') return <RulesPage />;
  if (route === 'archive') return <ArchivePage />;
  if (route === 'milhar') return <MilharPage />;
  if (route === 'milhar.ledger') return <MilharLedgerPage />;
  if (route === 'milhar.stats') return <MilharStatsPage />;
  if (route === 'milhar.hora') return <MilharPage tier="5" />;
  if (route === 'milhar.hora.ledger') return <MilharLedgerPage tier="5" />;
  if (route === 'milhar.hora.stats') return <MilharStatsPage tier="5" />;
  if (route === 'sorteios') return <SorteiosPage />;
  if (route === 'sorteios.history') return <SorteiosHistoryPage />;
  if (isAuthRoute(route)) {
    return (
      <>
        <LobbyNextPage />
        <BottomNav />
      </>
    );
  }
  if (route === 'lobby' || route === 'lobby.next') {
    return (
      <>
        <LobbyNextPage />
        <BottomNav />
      </>
    );
  }
  // The hall this replaced. Kept mounted at #/hall-legacy so the swap can be
  // compared against — and undone — without a deploy.
  if (route === 'lobby.legacy') {
    return (
      <>
        <LobbyPage />
        <BottomNav />
      </>
    );
  }
  if (route === 'pedidos.unpaid') {
    return (
      <>
        <UnpaidDepositsPage />
        <BottomNav />
      </>
    );
  }
  if (route === 'wallet' || route === 'wallet.tickets' || route === 'pedidos') {
    return (
      <>
        <WalletPage />
        <BottomNav />
      </>
    );
  }
  if (route === 'leaderboard') {
    return (
      <>
        <LeaderboardPage />
        <BottomNav />
      </>
    );
  }
  if (route === 'avisos' || route === 'avisos.faq' || route === 'avisos.help' || route === 'avisos.feedback') {
    return (
      <>
        <AvisosPage />
        <BottomNav />
      </>
    );
  }
  if (route === 'rewards' || route === 'me.activities') {
    return (
      <>
        <RewardsPage />
        <BottomNav />
      </>
    );
  }
  if (route === 'me.audit') {
    return (
      <>
        <AuditProgressPage />
        <BottomNav />
      </>
    );
  }
  if (route === 'me.follows') {
    return (
      <>
        <FollowsPage />
        <BottomNav />
      </>
    );
  }
  if (route === 'me.history') {
    return (
      <>
        <TransactionHistoryPage />
        <BottomNav />
      </>
    );
  }
  if (route === 'me' || route.startsWith('me.') || route === 'bet-history') {
    return (
      <>
        <MePage />
        <BottomNav />
      </>
    );
  }
  return (
    <>
      <LobbyNextPage />
      <BottomNav />
    </>
  );
}

type UsernameReturnTarget = {
  route: PlayerRoute;
  activeRoomId: string | null;
  activeOrderId: string | null;
  activeProfileUserId: string | null;
  activeArchiveRoundNumber: string | null;
  walletTargetAction: WalletTargetAction;
  meSecurityTargetChannel: MeSecurityTargetChannel;
  walletTicketsTargetView: WalletTicketsTargetView;
  walletTicketsResultId: string | null;
};

function captureUsernameReturnTarget(): UsernameReturnTarget {
  const state = useAppStore.getState();
  return {
    route: state.route,
    activeRoomId: state.activeRoomId,
    activeOrderId: state.activeOrderId,
    activeProfileUserId: state.activeProfileUserId,
    activeArchiveRoundNumber: state.activeArchiveRoundNumber,
    walletTargetAction: state.walletTargetAction,
    meSecurityTargetChannel: state.meSecurityTargetChannel,
    walletTicketsTargetView: state.walletTicketsTargetView,
    walletTicketsResultId: state.walletTicketsResultId,
  };
}

function restoreUsernameReturnTarget(target: UsernameReturnTarget | null) {
  const store = useAppStore.getState();
  if (!target || isAuthRoute(target.route)) {
    store.setRoute('lobby', { replace: true });
    return;
  }

  store.setRoute(target.route, { replace: true });
  if (target.route === 'room') store.setActiveRoomId(target.activeRoomId);
  if (target.route === 'pedidos' || target.route === 'pedidos.unpaid' || target.route === 'me.history') store.setActiveOrderId(target.activeOrderId);
  if (target.route === 'leaderboard') store.setActiveProfileUserId(target.activeProfileUserId, { replace: true });
  if (target.route === 'archive') store.setActiveArchiveRoundNumber(target.activeArchiveRoundNumber, { replace: true });
  if (target.route === 'wallet') store.setWalletTargetAction(target.walletTargetAction, { replace: true });
  if (target.route === 'me.security') store.setMeSecurityTargetChannel(target.meSecurityTargetChannel, { replace: true });
  if (target.route === 'wallet.tickets') store.setWalletTicketsTarget(target.walletTicketsTargetView, target.walletTicketsResultId, { replace: true });
}

function isAuthRoute(route: PlayerRoute) {
  return route === 'auth' || route.startsWith('auth.');
}

function isPublicRoute(route: PlayerRoute) {
  return route === 'avisos.faq' || route === 'avisos.help';
}

function userRequiresUsernameSetup(user: ReturnType<typeof useAuthStore.getState>['user']) {
  return !!(user?.requires_username_setup ?? user?.requiresUsernameSetup);
}
